<template>
  <div>
    <!-- Days toggler for mobile -->
    <div class="mobile-days-toggler mb-3">
      <days-count-toggler v-model="days" />
    </div>

    <template v-if="!loading">
      <v-card class="mb-3" v-for="item in items" :key="item.id">
        <div class="list-mobile">
          <div class="list-mobile__top">
            <div class="list-mobile__title">{{ item ? item.name : '-' | shortstr }}</div>
            <div class="list-mobile__button" v-if="item.recipe && !isConsultant">
              <v-btn icon @click="openEdit">
                <v-icon font-size="1" icon="cog"></v-icon>
              </v-btn>
            </div>
          </div>
          <div class="list-mobile__content">
            <div class="list-mobile__item">
              <div class="list-mobile__item-label">{{ 'base.daily_consumption' | translate }}</div>
              <div class="list-mobile__item-value">
                <span v-if="wait" class="skeleton-inline"></span>
                <template v-else>
                  <span>{{ Math.floor((item.daily_feed_intake || 0) * days) }}</span>
                  <span> {{ 'base.kg' | translate }}</span>
                </template>
              </div>
            </div>
            <div class="list-mobile__item">
              <div class="list-mobile__item-label">{{ 'base.stock_rest' | translate }}</div>
              <div class="list-mobile__item-value">{{ item.total_amount | floor }} {{ 'base.kg' | translate }}</div>
            </div>
            <div class="list-mobile__item">
              <div class="list-mobile__item-label">{{ 'base.dry_metter' | translate }}</div>
              <div class="list-mobile__item-value">
                {{ item.is_animalfeed && item.dry_matter > 0 ? (item.dry_matter).toFixed(2) : item.dry_matter }} {{
                  'base.kg' | translate }}
              </div>
            </div>
            <div class="list-mobile__item">
              <div class="list-mobile__item-label">{{ 'base.price' | translate }}</div>
              <div class="list-mobile__item-value">{{ item.total_price | floor }} {{ 'base.currency' | translate }}
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </template>

    <template v-else>
      <v-card class="mb-3" v-for="i in 5" :key="i">
        <div class="list-mobile list-mobile--skeleton">
          <div class="list-mobile__top mb-1">
            <div class="list-mobile__title-skeleton"></div>
          </div>
          <div class="list-mobile__content">
            <div class="list-mobile__item" v-for="j in 4" :key="`${i}-${j}`">
              <div class="list-mobile__item-skeleton list-mobile__item-skeleton--type-1"></div>
              <div class="list-mobile__item-skeleton list-mobile__item-skeleton--type-1"></div>
            </div>
          </div>
        </div>
      </v-card>
    </template>

    <ui-pagination v-if="showPagination" v-model="page" :page-count="pageCount" @page-change="pageChangeHandler" />
    
    <v-dialog v-model="dialog" max-width="200px">
      <v-card>
        <v-card-title>
          <h2 class="card-title">{{ $t('base.information') }}</h2>
        </v-card-title>
        <v-card-content>
          <div class="mb-1">
            <h3>{{ $t('base.information_desktop_kombi') }}</h3>
          </div>
        </v-card-content>
        <v-card-actions>
          <v-btn @click="dialog = false">{{ $t('base.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
   
  </div>
</template>

<script>
import api from '@/api'
import { mapActions, mapGetters } from 'vuex'
import paginationMixin from '@/mixins/pagination.mixin'
import CreateRecipeDialog from '../modals/CreateRecipeDialog'
import MixFodderDialog from '../modals/MixFodderDialog'
import DaysCountToggler from '../../ingredients/table/DaysCountToggler.vue'

export default {
  name: 'FodderTableMobile',

  mixins: [paginationMixin],

  components: {
    CreateRecipeDialog,
    MixFodderDialog,
    DaysCountToggler
  },

  data () {
    return {
      dialog: false,
      showCreateRecipeDialog: false,
      showMixDialog: false,
      currentItem: null,
      currentFodder: null,
      loading: true,
      items: [],
      wait: true,
      days: 1
    }
  },

  computed: {
    ...mapGetters('ingredients', [
      'getDailyFeedIntakesData'
    ]),
    dailyFeedIntakesData () {
      return this.getDailyFeedIntakesData
    },
    allowCraft () {
      return this.hasPermission('animalfeed_delivery', 'add')
    },
    isConsultant () {
      // Implement this computed property based on your user role logic
      return false
    }
  },

  created () {
    this.loadData()
  },

  methods: {
    ...mapActions('recipes', ['selectRecipe']),

    openEdit () {
      this.dialog = true
    },

    async loadData () {
      this.loading = true
      try {
        const params = {
          farm: this.$route.params.farm_id,
          is_animalfeed: true
        }

        // Check if page query parameter exists and add it
        if (this.$route.query.page) {
          params.page = this.$route.query.page
        }

        if (this.$route.query.stock) {
          params.stock = this.$route.query.stock
        }

        const response = await api.ingredients.list(params)
        this.items = response.data
        this.setupPagination()

        await this.loadDailyFeedIntakes()
      } catch (error) {
        console.error('Error loading fodder data:', error)
      } finally {
        this.loading = false
      }
    },

    async loadDailyFeedIntakes () {
      this.wait = true
      try {
        const { data } = await api.ingredients.dailyFeedIntakes({
          farm: this.$route.params.farm_id,
          is_animalfeed: true,
          page: this.page,
          offset: (this.page - 1) * 100 || null
        })
        this.items.forEach(item => {
          const updatedItem = data.find(d => d.id === item.id)
          if (updatedItem) {
            item.daily_feed_intake = updatedItem.daily_feed_intake
          }
        })
        this.wait = false
        this.$forceUpdate()
      } catch (error) {
        console.error('Error loading daily feed intakes:', error)
        this.wait = false
      }
    },

    handleDialogUpdate () {
      this.loadData()
    },

    openCraftDialog (item) {
      if (item.recipe && this.allowCraft) {
        this.currentItem = item
        this.showMixDialog = true
      } else {
        this.currentFodder = item
        this.showCreateRecipeDialog = true
      }
    }
  },

  watch: {
    page () {
      this.loadData()
    },
    dailyFeedIntakesData () {
      this.loadDailyFeedIntakes()
    }
  }
}
</script>

<style scoped>
.mobile-days-toggler {
  display: flex;
  justify-content: flex-start;
  padding: 0 1rem;
}

.list-mobile__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.list-mobile__title {
  font-size: 1.6rem;
  font-weight: bold;
}

.list-mobile__content {
  display: flex;
  flex-direction: column;
}

.list-mobile__item {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}

.list-mobile__item-label {
  color: #979797;
  font-size: 1.5rem;
  font-weight: 500;
}

.list-mobile__item-value {
  font-weight: 500;
}

.skeleton-inline {
  display: inline-block;
  width: 4rem;
  height: 1.5rem;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  vertical-align: middle;
}

.list-mobile--skeleton .list-mobile__title-skeleton {
  height: 24px;
  width: 70%;
  background-color: #eee;
  border-radius: 4px;
}

.list-mobile--skeleton .list-mobile__item-skeleton {
  height: 16px;
  background-color: #eee;
  border-radius: 4px;
}

.list-mobile--skeleton .list-mobile__item-skeleton--type-1 {
  width: 40%;
}

.skeleton {
  animation: skeleton-loading 1s linear infinite alternate;
}

@keyframes skeleton-loading {
  0% {
    background-color: rgba(238, 238, 238, 0.7);
  }

  100% {
    background-color: rgba(238, 238, 238, 0.95);
  }
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>